// Phase 3 — Persists the last-picked animation preset per *pack* in localStorage.
// Storage key is `${avatarId}:${packId}` so each pack remembers its own
// selection independently. Phase 2 keys (avatarId only) are kept as a
// read-fallback during transition.
//
// Returns/accepts free-form string preset ids ('idle', 'meshy-dance-22', …)
// because Phase 3 widened the preset taxonomy beyond Tripo's 10-string enum.

const KEY = 'qavatar.lastPickedAnimation';

type Store = Record<string, string>;

function load(): Store {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {};
  }
}

/**
 * Read the last-picked preset for a storage key.
 * Falls back to a Phase 2-style avatarId-only key if the avatar-only
 * legacy entry exists (during transition).
 */
export function getLastPickedAnimation(storageKey: string): string | undefined {
  const store = load();
  if (store[storageKey]) return store[storageKey];
  // Phase 2 backward-compat: if storageKey looks like 'avatarId:packId',
  // try the avatarId portion alone.
  const colon = storageKey.indexOf(':');
  if (colon > 0) {
    const fallback = storageKey.slice(0, colon);
    if (store[fallback]) return store[fallback];
  }
  return undefined;
}

export function setLastPickedAnimation(storageKey: string, preset: string): void {
  try {
    const store = load();
    store[storageKey] = preset;
    localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    // localStorage may be unavailable (e.g. Safari private mode). Best-effort only.
  }
}
